import { useMemo, useRef } from 'react';
import _ from 'lodash';
import { faMoneyCheckDollar } from '@fortawesome/pro-regular-svg-icons';
import { classNames } from '@progress/kendo-react-common';
import { 
    DateTabs,
    HasAdjustmnentsFilter,
    Icon,
    PaginatedList,
    PaymentCard,
    PaymentSortFilter,
    PaymentStatusFilter,
    SearchInput,
    SuspenseBoundary,
} from 'components';
import { useAccountParam, useVisibility } from 'hooks';
import { useFilteredPayments, usePaymentFilter, useRefreshFilteredPayments, useUserInfo } from 'state';
import styles from './PaymentsPage.module.scss';

export default function PaymentsPage() {
    const accountId = useAccountParam();
    const { isInternalUser } = useUserInfo();
    const {
        dateRange,
        setDateRange,
        paymentStatus,
        hasAdjustments,
        sort,
        setPaymentStatus,
        setHasAdjustments,
        searchTerm,
        setSearchTerm,
        setSort
    } = usePaymentFilter();
    const refresh = useRefreshFilteredPayments(accountId);

    const headerRef = useRef<HTMLDivElement | null>(null);
    const shouldShowCombinedHeader = useVisibility(headerRef);

    const Headers = useMemo(() => (
        <div className={styles.pageHeader}>
            {shouldShowCombinedHeader ? 
                <div className={styles.title}>               
                    <span>
                        <Icon icon={faMoneyCheckDollar}/> 
                    </span>
                    <span>
                        Payments 
                    </span>
                </div>
                : <></>}
            <DateTabs
                dateRange={dateRange}
                setDateRange={setDateRange}
            />
        </div>
    ), [dateRange, setDateRange, shouldShowCombinedHeader]);

    const Filters = useMemo(() => (
        <div className={styles.filters}>
            {isInternalUser && 
                <PaymentStatusFilter
                    paymentStatus={paymentStatus}
                    onStatusChange={setPaymentStatus}
                />
            }
            <HasAdjustmnentsFilter
                onStatusChange={setHasAdjustments}
                hasAdjustments={hasAdjustments}
            />
            <SearchInput
                onValueChange={setSearchTerm}
                value={searchTerm}
            />
            <PaymentSortFilter
                paymentSort={sort}
                onPaymentSortChange={setSort}
            />
        </div>
    ),[hasAdjustments, isInternalUser, paymentStatus, searchTerm, setHasAdjustments, setPaymentStatus, setSearchTerm, setSort, sort]);

    const FiltersAndHeader = useMemo(() =>
        <div className={classNames(styles.fixedHeaderAndFilter, {[styles.hidden] : shouldShowCombinedHeader })}>
            {Filters}
            {Headers}
        </div>
    ,[Filters, Headers, shouldShowCombinedHeader]); 

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={classNames({[styles.hidden] : !shouldShowCombinedHeader})} ref={headerRef}>
                    {Headers}
                </div>
                {FiltersAndHeader}
                <div className={styles.content}>
                    <div className={classNames(styles.filters, {[styles.hidden] : !shouldShowCombinedHeader})}>
                        {Filters}
                    </div>
                    {accountId ? (
                        <SuspenseBoundary
                            errorMessage={`Error retrieving payments for account ${accountId}`}
                            onReset={refresh}
                        >
                            <PaymentsComponent />
                        </SuspenseBoundary>
                        ) 
                    : <div className={styles.message}>Please select an account to view payments.</div>
                    }
                </div>
            </div>
        </div>
    )
}

const PaymentsComponent = () => {
    const accountId = useAccountParam();
    const payments = useFilteredPayments(accountId);

    return !_.isEmpty(payments) ? (
        <PaginatedList
            items={payments}
            componentKey='Payments'
            resetKey={accountId}
        >
            {
                (item) =>
                    <PaymentCard
                        key={item.paymentIdRef}
                        paymentSummary={item}
                    />
            }
        </PaginatedList>
    ) : <div className={styles.message}>No payments found for the selected date range and filters.</div>
};